/**
 * Council Trail Task 6.6 — MessageRow.
 *
 * Lens-aware wrapper around MessageBubble. On-lens renders the bubble
 * unchanged; off-lens renders a PeekStripBlock (inline expand).
 *
 * P3 SP1 — visibility now comes from the pure lensVisibility predicate
 * over a LensSnapshot (utils/lensVisibility.ts — the matrix is its test):
 * persona modes filter peers by set membership (operator always on);
 * TRAIL mode filters EVERYTHING by message.metadata.trail_id, operator
 * included (off-trail operator messages render the neutral "+ You" strip).
 * No snapshot (admin/work surfaces) → everything on.
 */
import React from 'react';

import type { PeerMetadata } from '../../types/council';
import { lensVisibility, type LensSnapshot } from '../../utils/lensVisibility';
import { MessageBubble } from './MessageBubble';
import { PeekStripBlock } from './PeekStripBlock';

export interface MessageRowProps {
  message: any;
  showTimestamp?: boolean;
  modelName?: string;
  sessionId?: string;
  personaId?: string;
  personaName?: string;
  /** Peer-author metadata for this message (undefined if operator-authored). */
  peerMetadata?: PeerMetadata;
  /** Composed lens snapshot (useLensSnapshot); null = no lens = all on. */
  lensSnapshot?: LensSnapshot | null;
}

function _isOnLens(
  message: any,
  peerMetadata: PeerMetadata | undefined,
  lensSnapshot: LensSnapshot | null | undefined,
): boolean {
  if (!lensSnapshot) return true; // surfaces without a lens (admin/work chat)
  const trailId = (message?.metadata as any)?.trail_id ?? null;
  const subject = peerMetadata
    ? { kind: 'peer' as const, personaId: peerMetadata.personaId, trailId }
    : { kind: 'operator' as const, trailId };
  return lensVisibility(lensSnapshot, subject) === 'on';
}

export function MessageRow({
  peerMetadata,
  lensSnapshot,
  ...bubbleProps
}: MessageRowProps) {
  const onLens = _isOnLens(bubbleProps.message, peerMetadata, lensSnapshot);
  if (onLens) {
    return <MessageBubble {...(bubbleProps as any)} />;
  }
  return (
    <PeekStripBlock
      peerMetadata={peerMetadata}
      message={bubbleProps.message}
      bubbleProps={bubbleProps as Record<string, unknown>}
    />
  );
}
